import React, { useId } from "react";
/** Intentional vector product artwork. Accompanying name/status supplies accessible text. */
export default function AchievementBadge({
  category,
  family,
  threshold,
  earned = false,
  exceptional = false,
}) {
  const id = useId().replace(/:/g, ""),
    colors = {
      membership: "#8399b2",
      heritage: "#92a797",
      golf: "#92a797",
      racquet: "#9b98b4",
      events: "#8399b2",
      spa: "#a3b4aa",
      academy: "#a3a1b7",
    },
    accent = earned ? colors[category] : "#677587";
  const art = {
    heritage: (
      <>
        <path d="M60 79V39m0 22L40 49m20 21 23-19M60 48 45 33m15 12 14-15" />
        <path d="M35 47c-8-15 15-23 19-8M76 47c19-15 25 7 10 10M55 32c-4-19 19-22 22-6M48 31c-9-11-21 1-15 11" />
        <path d="M44 81h32" />
      </>
    ),
    golf: (
      <>
        <path d="M52 81V31l29 10-29 11" />
        <ellipse cx="60" cy="84" rx="24" ry="5" />
        <circle cx="76" cy="70" r="8" />
        <path d="m74 68 1 1m4 1-1 1m-4 3 1-1" />
      </>
    ),
    racquet: (
      <>
        <ellipse
          cx="45"
          cy="44"
          rx="12"
          ry="19"
          transform="rotate(-35 45 44)"
        />
        <ellipse cx="76" cy="44" rx="12" ry="19" transform="rotate(35 76 44)" />
        <path d="m55 59 21 29m-9-29L46 88M36 42l19 8m-17-15 19 9m8 1 20-9m-22 15 21-9" />
      </>
    ),
    events: (
      <>
        <path d="M34 41h52v40H34zM34 51h52M46 34v14m28-14v14" />
        <circle cx="51" cy="62" r="4" />
        <circle cx="70" cy="62" r="4" />
        <path d="M44 74q7-12 14 0m5 0q7-12 14 0" />
      </>
    ),
    spa: (
      <>
        <path d="M60 82C28 69 27 46 36 40q25 5 24 42Zm0 0c32-13 33-36 24-42Q59 45 60 82Z" />
        <path d="M60 72Q40 45 60 27q20 18 0 45Z" />
      </>
    ),
    academy: (
      <>
        <path d="M60 44q-14-12-30-6v43q16-6 30 6 14-12 30-6V38q-16-6-30 6v43M38 48l14 4m-14 6 14 4m16-10 14-4m-14 14 14-4" />
      </>
    ),
  };
  return (
    <svg
      className={`mp-achievement-badge ${earned ? "is-earned" : ""}`}
      data-tier={earned ? threshold : "none"}
      viewBox="0 0 120 128"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={`${id}edge`} x2="1" y2="1">
          <stop stopColor="#d2d7dd" />
          <stop offset=".35" stopColor="#788694" />
          <stop offset=".65" stopColor="#b2bcc6" />
          <stop offset="1" stopColor="#566675" />
        </linearGradient>
        <linearGradient id={`${id}face`} x2=".6" y2="1">
          <stop stopColor={accent} />
          <stop offset="1" stopColor="#243447" />
        </linearGradient>
        <filter id={`${id}relief`}>
          <feDropShadow
            dx="0"
            dy="2"
            stdDeviation="1.3"
            floodColor="#071422"
            floodOpacity=".65"
          />
        </filter>
      </defs>
      <path
        d="M60 5 105 23v47q-1 29-45 51Q16 99 15 70V23Z"
        fill={`url(#${id}edge)`}
      />
      <path
        d="M60 10 100 27v43q-2 26-40 45Q22 96 20 70V27Z"
        fill={`url(#${id}face)`}
      />
      <path
        d="M60 15 95 30v39q-2 23-35 40Q27 92 25 69V30Z"
        fill="none"
        stroke="#d9e0e6"
        strokeOpacity=".25"
      />
      {category === "membership" ? (
        <>
          <path
            d="M39 38q21-14 42 0M39 87q21 14 42 0"
            stroke="#d9e0e6"
            strokeOpacity=".6"
            fill="none"
          />
          <text
            x="60"
            y="77"
            textAnchor="middle"
            fill="#f1f2f5"
            fontSize={threshold >= 10 ? 36 : 42}
            fontFamily="inherit"
            filter={`url(#${id}relief)`}
          >
            {threshold || "—"}
          </text>
        </>
      ) : (
        <g
          fill="none"
          stroke="#e0e5e9"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter={`url(#${id}relief)`}
        >
          {family === "golf_ace" ? (
            <>
              <circle cx="60" cy="54" r="23" />
              <text
                x="60"
                y="63"
                textAnchor="middle"
                fill="#e0e5e9"
                stroke="none"
                fontSize="28"
              >
                1
              </text>
              <path d="M38 83h44" />
            </>
          ) : family === "golf_under_par" ? (
            <>
              <path d="M60 30v45m-17-17 17 17 17-17M35 87h50" />
              <circle cx="80" cy="35" r="7" />
            </>
          ) : (
            art[category]
          )}
        </g>
      )}
      {exceptional ? (
        <path
          d="m94 15 3 7 8 1-6 5 2 8-7-4-6 4 1-8-6-5 8-1Z"
          fill="#b5b6c9"
          stroke="#e1e4ed"
        />
      ) : null}
      {category !== "membership" && !exceptional ? (
        <text
          x="60"
          y="104"
          textAnchor="middle"
          fill="#eef0f3"
          fontSize="25"
          fontWeight="650"
          fontFamily="inherit"
        >
          {threshold || "—"}
        </text>
      ) : null}
    </svg>
  );
}
